// Who is asking, for the purposes of a rollout and nothing else.
//
// There are no accounts here, so "a user" is a browser. This is a random id
// minted once and kept in `localStorage`, and its only job is to be *the same
// tomorrow* — a rollout that re-rolled per visit would show a feature to a
// different quarter of people every day and measure nothing.
//
// **It identifies nobody.** It is not derived from anything about the person,
// it is never sent anywhere except as the input to a local hash, and clearing
// it costs the visitor nothing but a fresh draw. That is the whole of why it
// can be a random number rather than a fingerprint: the question a rollout
// asks is "am I in the quarter", not "who are you".

import { FLAGS, type Flag, type FlagId, bandsFor, isOn, variantOf } from "./flags";

const KEY = "chessfactory.visitor";

/**
 * `Math.random` here is correct and would be an error two directories over.
 *
 * `src/engine/rules/` and `src/engine/ai/` are lint-barred from it because two clients
 * deriving one board from one log must roll the same numbers. This is the
 * opposite requirement: the id has to be *unlike* everybody else's, it never
 * reaches a rule, and a replay does not reproduce it because nothing about it
 * is in the log. Same licence `freshSeed` and `ui/playerName.ts` take.
 */
const mint = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * This browser's rollout key, minted on first use.
 *
 * Storage that refuses to answer — private browsing, a blocked origin, no DOM
 * at all — gets a fresh key per call rather than an exception. That visitor
 * lands in a different bucket on every read, which is the honest outcome: we
 * cannot keep an experiment stable for somebody whose browser will not hold
 * one, and a feature flickering is a smaller failure than a page that will not
 * load.
 */
export function visitorKey(): string {
  try {
    const held = window.localStorage.getItem(KEY);
    if (held) return held;
    const fresh = mint();
    window.localStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    return mint();
  }
}

/**
 * A local override, for looking at the other side of a rollout.
 *
 * `?flag=score-panel` forces one on, `?flag=-score-panel` forces it off, and
 * `?flag=wave-delay=5` forces a multi-way flag onto the arm whose value is
 * `5` — all for this tab. **Deliberately in the URL rather than a menu**: what
 * it is for is handing somebody a link to the thing you want them to look at,
 * which is the same argument `?room=` and `?pack=` already make — and a
 * control in the interface would be a permanent piece of chrome describing an
 * experiment that is meant to be temporary. (`ExperimentsBadge` offers dev
 * builds buttons for this, and they *write this URL* rather than holding any
 * override of their own — one mechanism, still shareable as a link.)
 *
 * It cannot affect anybody else's game, which is what makes it safe to leave
 * in production: a flag reaches presentation only, so the worst a forced one
 * does is show its holder a screen.
 */
export function forced(id: FlagId): boolean | string | null {
  try {
    return forcedIn(new URLSearchParams(window.location.search).getAll("flag"), id);
  } catch {
    return null;
  }
}

/**
 * The override grammar, pure so it is testable without a window.
 *
 * `id` is on, `-id` is off, `id=value` names an arm — the *text* of one, since
 * a URL has no numbers. What the value names is not this function's business:
 * matching it against the flag's arms happens where the arms are known, and an
 * unknown value is refused there rather than defaulted, so a typo falls back
 * to the bucket instead of quietly forcing the first arm.
 */
export function forcedIn(asked: readonly string[], id: string): boolean | string | null {
  for (const entry of asked) {
    if (entry === id) return true;
    if (entry === `-${id}`) return false;
    if (entry.startsWith(`${id}=`)) return entry.slice(id.length + 1);
  }
  return null;
}

/**
 * The table's entry, read through `Flag` rather than the narrowed literal
 * type: `as const satisfies` keeps each entry's exact shape, so `variants` is
 * simply absent from the boolean ones and the union has no such property.
 */
const flagFor = (id: FlagId): Flag | undefined => FLAGS.find((entry) => entry.id === id);

/** The arm whose text matches an override's value, or undefined for none. */
export const armNamed = <T extends string | number>(
  arms: readonly T[] | undefined,
  text: string
): T | undefined => arms?.find((arm) => String(arm) === text);

/**
 * Is this flag on, for this browser, under these rollouts?
 *
 * The rollouts come from the server (`/api/flags`) so they can be changed
 * without a rebuild; everything else is local, so a read costs nothing and
 * works with the network down.
 */
export function flagIsOn(
  id: FlagId,
  rollouts: Record<string, number> | null,
  key = visitorKey()
): boolean {
  const override = forced(id);
  if (override === true || override === false) return override;
  // A named arm means "in the experiment" — but only when the flag actually
  // has that arm. An unknown value falls through to the bucket, the same
  // refusal `flagVariant` makes about it.
  if (override !== null && armNamed(flagFor(id)?.variants, override) !== undefined) return true;
  // **The shipped default until the server answers**, never off. A flag that
  // read false for the first moment of every page would make a feature at 100%
  // flicker in on load, which is the thing a rollout is supposed to look
  // nothing like.
  const rollout = rollouts?.[id] ?? FLAGS.find((flag) => flag.id === id)?.rollout ?? 0;
  return isOn(id, key, rollout);
}

/**
 * Which arm of a multi-way flag this browser is on, or `fallback` for none.
 *
 * The boolean read's twin, and it shares its two properties for the same
 * reasons: an override wins (which for an arm means "on" is the *first* arm,
 * since a URL cannot name one and the first is the one being tried), and the
 * shipped default answers until the deployment has, so nothing flickers.
 *
 * **One bucket, so the arms cannot overlap.** Two boolean flags at 30% each
 * are salted independently and put about 9% of people in both, with no honest
 * answer to what those people were shown.
 */
export function flagVariant<T extends string | number, F>(
  id: FlagId,
  fallback: F,
  rollouts: Record<string, number> | null,
  key = visitorKey()
): T | F {
  const flag = flagFor(id);
  const arms = flag?.variants as readonly T[] | undefined;
  if (!arms || arms.length === 0) return fallback;

  const override = forced(id);
  if (override === false) return fallback;
  if (override === true) return arms[0];
  // `?flag=id=value` names the arm itself. An unknown value is refused rather
  // than defaulted — a typo falls back to the bucket, never to the first arm.
  if (override !== null) {
    const named = armNamed(arms, override);
    if (named !== undefined) return named;
  }

  const rollout = rollouts?.[id] ?? flag!.rollout;
  return variantOf(id, key, bandsFor(rollout, arms), fallback);
}
